import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

const NoteHeaderContent = ({ item }) => {
  const dateStr = new Date(item.createdate).toLocaleString();

  const pills = useMemo(() => {
    const list = [];

    const isSOAP = /SOAP|S\.O\.A\.P\./i.test(item.text_only || '');
    if (isSOAP) {
      list.push({
        id: 'SOAP',
        label: 'SOAP',
        color: '#3B4C66',
      });
    }

    const sortedTags = (item.tags || []).sort(
      (a, b) => (a.priority ?? 99) - (b.priority ?? 99)
    );

    sortedTags.forEach(tag => {
      if (!tag?.id) return;
      list.push({
        id: String(tag.id),
        label: tag.label_name,
        color: tag.color || '#ccc',
      });
    });

    return list;
  }, [item]);

  const [highlightIndexes, setHighlightIndexes] = useState({});
  const [activePillId, setActivePillId] = useState(null);
  const [highlightCounts, setHighlightCounts] = useState({});

  useLayoutEffect(() => {

    let rafId = requestAnimationFrame(() => {
      const counts = {};

      pills.forEach(pill => {
        if (!pill?.id) return;

        const selector = `.highlightText[data-id="${CSS.escape(pill.id)}"]`;
        const matches = document.querySelectorAll(selector);

        counts[pill.id] = matches.length;
      });

      setHighlightCounts(counts);
    });

    return () => cancelAnimationFrame(rafId);
  }, [pills, item]);

  const scrollToNextHighlight = id => {
    if (!id) {
      return;
    }

    const selector = `.highlightText[data-id="${CSS.escape(id)}"]`;
    const matches = Array.from(document.querySelectorAll(selector));

    if (!matches.length) return;

    document
      .querySelectorAll('.highlightText.current-highlight')
      .forEach(el => el.classList.remove('current-highlight'));

    setHighlightIndexes(prev => {
      const next = { ...prev };

      if (activePillId !== id) {
        if (activePillId) next[activePillId] = 0;
        next[id] = 0;
        setActivePillId(id);
      } else {
        next[id] = (next[id] + 1) % matches.length;
      }

      const el = matches[next[id]];
      if (el) {
        el.classList.add('current-highlight');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        el.classList.add('active-highlight');
        setTimeout(() => el.classList.remove('active-highlight'), 1500);
      }

      return next;
    });
  };

  return (
    <div className="modal-note-header">
      <div className="modal-note-date">{dateStr}</div>

      {pills.length > 0 && (
        <div className="modal-note-pills">
          {pills.map(pill => {
            if (!pill?.id) return null;

            const total = highlightCounts[pill.id] ?? 0;
            const current =
              activePillId === pill.id &&
              highlightIndexes[pill.id] !== undefined
                ? highlightIndexes[pill.id] + 1
                : 0;

            return (
              <span
                key={pill.id}
                className="modal-note-pill"
                style={{
                  backgroundColor: pill.color,
                  opacity: total === 0 ? 0.5 : 1,
                  cursor: total === 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={() =>
                  total > 0 && scrollToNextHighlight(pill.id)
                }
                title={
                  total > 0
                    ? `Cycle through "${pill.label}" highlights`
                    : `No highlights for "${pill.label}"`
                }
              >
                {pill.label}
                {current > 0 && total > 0 && (
                  <span className="modal-note-pill-badge">
                    {current} / {total}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NoteHeaderContent;